import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../Context";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import { Tween, Easing, Group } from '@tweenjs/tween.js';
import Gizmo from "./Gizmo";
import ViewHeader from "./ViewHeader";

function View3D() {
    const { state } = useContext(AppContext);
    const { dispatch } = useContext(AppContext);
    const userHit = useRef(false);
    const [activeTrans, setActiveTrans] = useState("rotate");
    const [trans, setTrans] = useState("spin");
    const { showTooltip, hideTooltip } = useContext(AppContext);
    const [shirtLoaded, setShirtLoaded] = useState(false);
    const colors = [
        { id: 'blue', color: 'blue', hex: 0x1552af },
        { id: 'lightBlue', color: 'light blue', hex: 0x7a9ddc },
        { id: 'brown', color: 'brown', hex: 0x5a3f2b },
        { id: 'gold', color: 'gold', hex: 0xae8a4e },
        { id: 'white', color: 'white', hex: 0xebe6e2 },
        { id: 'gray', color: 'gray', hex: 0x9b9799 },
        { id: 'black', color: 'black', hex: 0x07060b },
        { id: 'navy', color: 'navy blue', hex: 0x000080 },
        { id: 'green', color: 'green', hex: 0x206a28 },
        { id: 'red', color: 'red', hex: 0xce0820 },
        { id: 'burgundy', color: 'burgundy', hex: 0x800020 },
        { id: 'lavender', color: 'lavender', hex: 0x815e98 },
        { id: 'lightPink', color: 'light pink', hex: 0xefc3b8 },
        { id: 'pink', color: 'pink', hex: 0xfb9d9e },
        { id: 'orange', color: 'orange', hex: 0xdb491d },
        { id: 'purple', color: 'purple', hex: 0x411847 },
    ];
    const mono = useRef({
        cuff: { x: 0.53, y: -0.65, z: 1.03, ax: -90, ay: 0, az: -30 },
        body: { x: 0.3, y: 0, z: 0.5, ax: 0, ay: 15, az: 0 },
        pocket: { x: -0.5, y: 0.75, z: 0.5, ax: 0, ay: -18, az: 10 },
        placket: { x: -0.085, y: -1.6, z: 0.58, ax: 5, ay: -10, az: 0 },
        collarIn: { x: -0.05, y: 1.93, z: -0.3, ax: 0, ay: 0, az: 0 },
        collarOut: { x: 0.06, y: 1.98, z: -0.36, ax: 29, ay: 180, az: 0 },
    });
    const monogram = useRef(null);
    const mountRef = useRef(null);
    const viewFooter = useRef(null);
    const shirtRef = useRef(null);
    const modelRef = useRef(null);
    const monoBox = useRef(null);
    const modelLoaded = useRef(false);
    const tweenGroup = useRef(new Group());
    const userInteraction = useRef(false);
    const modelScaleRef = useRef(4);
    const targetScaleRef = useRef(4);
    const scaleFactor = 0.5;
    const transform = useRef("rotate");
    const rotateByGizmo = useRef(false);
    const degree2Radian = Math.PI / 180;
    const radian2Degree = 180 / Math.PI;
    const anglesRef = useRef({ x: 0, y: 0 });
    const angle = useRef(0);
    const angleX = useRef(0);
    const angleY = useRef(0);
    const angleZ = useRef(0);
    const ax = useRef(0);
    const ay = useRef(0);
    const canvas = useRef(null);
    const bakeCanvas = useRef(null);
    const black100 = useRef(null);
    const black50 = useRef(null);
    const ctxBakeCanvas = useRef(null);
    const bakeImage = useRef(null);
    const bakeReady = useRef(false);
    const bakedFabric = useRef(null);
    const scene = useRef(null);
    const all = useRef(['ButtonArm', 'HoleArm', 'ButtonNeck', 'HoleNeck', 'Pocket1', 'Pocket2', 'Pleats', 'Placket', 'Neck', 'Shirt', 'CuffFrenchCurve', 'CuffStandardCurve', 'CuffStandardSquare', 'CuffStandardAngle', 'CuffFrenchSquare', 'CuffFrenchAngle', 'CollarWide', 'CollarHiddenButtonDown', 'CollarNormal', 'CollarSemiWide', 'CollarMandarian', 'CollarWing', 'CollarButtonDown', 'ButtonSet2', 'ButtonRound', 'ButtonSet1', 'CuffButton2', 'CuffButton1', 'CollarButton', 'HoleSet1', 'CuffHole2', 'CuffHole1', 'HoleSet2', 'CollarHole'])
    const collar3D = useRef(['CollarNormal', 'CollarWide', 'CollarSemiWide', 'CollarButtonDown', 'CollarHiddenButtonDown', 'CollarWing', 'CollarMandarian']);
    const cuff3D = useRef(['CuffButton1', 'CuffButton2', 'CuffHole1', 'CuffHole2', 'ButtonRound', 'CuffFrenchAngle', 'CuffFrenchCurve', 'CuffFrenchSquare', 'CuffStandardCurve', 'CuffStandardSquare', 'CuffStandardAngle']);
    const onlyCuff3D = useRef(['CuffFrenchAngle', 'CuffFrenchCurve', 'CuffFrenchSquare', 'CuffStandardCurve', 'CuffStandardSquare', 'CuffStandardAngle']);
    const pocket3D = useRef(['Pocket1', 'Pocket2']);
    const [monoLoc, setMonoLoc] = useState(mono.current.collarIn);
    const onMouseDownRef = useRef(null);
    const onMouseWheelRef = useRef(null);
    const onMouseMoveRef = useRef(null);
    const onMouseUpRef = useRef(null);
    const onKeyDownRef = useRef(null);
    const on3DTouchStart = useRef(null);
    const on3DTouchMove = useRef(null);
    const on3DTouchEnd = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const mdown = useRef(false);
    const xp = useRef(0);
    const yp = useRef(0);
    var touchPositions = useRef([]);
    var initialDistance = useRef(0);
    var initialScale = useRef(new THREE.Vector3(1, 1, 1));
    var modelScale = useRef(4);

    useEffect(() => {
        monoBox.current = new THREE.Group();
        const container = mountRef.current;
        scene.current = new THREE.Scene();
        scene.current.background = new THREE.Color(0xffffff);
        const camera = new THREE.PerspectiveCamera(40, container.clientWidth / container.clientHeight, 1, 4000);
        camera.position.set(0, 2, 25);
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
        renderer.setSize(container.clientWidth, container.clientHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        container.appendChild(renderer.domElement);
        canvas.current = renderer.domElement;
        const ambient = new THREE.AmbientLight(0xffffff, 1.5);
        const dirLight = new THREE.DirectionalLight(0xffffff, 2);
        dirLight.position.set(0, 50, 20);
        const dirLightBack = new THREE.DirectionalLight(0xffffff, 2);
        dirLightBack.position.set(0, 50, -20);
        const model = new THREE.Group();
        model.add(ambient, dirLight, dirLightBack);
        model.add(monoBox.current);
        scene.current.add(model);
        modelRef.current = model;
        loadModel();

        const handleResize = () => {
            const w = container.clientWidth;
            const h = container.clientHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
            const isFs = !!document.fullscreenElement;
            if (isFs) {
                onFullScreen();
                setIsFullscreen(true);
            } else {
                onNormScreen();
                setIsFullscreen(false);
            }
        };
        on3DTouchStart.current = (e) => {
            mdown.current = true;
            xp.current = e.touches[0].pageX;
            yp.current = e.touches[0].pageY;
            userInteraction.current = true;
            if (transform.current == 'rotate') {
                setTrans("rotate");
            } else if (transform.current == 'drag') {
                setTrans("drag");
            }
            if (e.touches.length === 2) {
                touchPositions.current[0] = new THREE.Vector2(e.touches[0].pageX, e.touches[0].pageY);
                touchPositions.current[1] = new THREE.Vector2(e.touches[1].pageX, e.touches[1].pageY);
                initialDistance.current = touchPositions.current[0].distanceTo(touchPositions.current[1]);
                if (model) {
                    initialScale.current.copy(model.scale);
                }
            }
        }
        on3DTouchMove.current = (e) => {
            if (e.touches[0].clientY > 0) {
                e.preventDefault();
            }
            if (e.touches.length === 2) {
                var currentDistance = new THREE.Vector2(e.touches[0].clientX, e.touches[0].clientY).distanceTo(new THREE.Vector2(e.touches[1].clientX, e.touches[1].clientY));
                var scale = currentDistance / initialDistance.current;
                if (model) {
                    modelScale.current = initialScale.current.x * scale;
                    if (modelScale.current < 0.5) {
                        modelScale.current = 0.5;
                    }
                    if (modelScale.current > 30) {
                        modelScale.current = 30;
                    }
                    model.scale.set(modelScale.current, modelScale.current, modelScale.current);
                    targetScaleRef.current = modelScale.current;
                }
            } else {
                if (mdown.current) {
                    if (transform.current == 'rotate') {
                        var xa = ax.current + e.touches[0].pageY - yp.current;
                        var ya = ay.current + e.touches[0].pageX - xp.current;
                        angleX.current = xa;
                        angleY.current = ya;
                    } else if (transform.current == 'drag') {
                        const deltaMove = {
                            x: e.touches[0].pageX - xp.current,
                            y: e.touches[0].pageY - yp.current,
                        };
                        var s = 0.05;
                        model.position.x += deltaMove.x * s;
                        model.position.y -= deltaMove.y * s;
                        xp.current = e.touches[0].pageX;
                        yp.current = e.touches[0].pageY;
                    }
                }
            }
        }
        on3DTouchEnd.current = (e) => {
            mdown.current = false;
            angleX.current = angleX.current % 360;
            angleY.current = angleY.current % 360;
            if (angleY.current < -180) {
                angleY.current = 360 + angleY.current;
            }
            if (angleX.current < -180) {
                angleX.current = 360 + angleX.current;
            }
            ax.current = angleX.current;
            ay.current = angleY.current;

            e.preventDefault();
            touchPositions.current.length = 0;
        }
        onMouseDownRef.current = (e) => {
            mdown.current = true;
            if (transform.current == 'rotate') {
                setTrans("rotate");
            } else if (transform.current == 'drag') {
                setTrans("drag");
            }
            xp.current = e.clientX;
            yp.current = e.clientY;
            userInteraction.current = true;
        };
        onMouseMoveRef.current = (e) => {
            if (mdown.current) {
                if (transform.current == 'rotate') {
                    var xa = ax.current + e.clientY - yp.current;
                    var ya = ay.current + e.clientX - xp.current;
                    angleX.current = xa;
                    angleY.current = ya;
                } else if (transform.current == 'drag') {
                    const deltaMove = {
                        x: e.clientX - xp.current,
                        y: e.clientY - yp.current,
                    };
                    model.position.x += deltaMove.x * 0.01;
                    model.position.y -= deltaMove.y * 0.01;
                    xp.current = e.clientX;
                    yp.current = e.clientY;
                }
            }
        };
        onMouseUpRef.current = () => {
            mdown.current = false;
            angleX.current = angleX.current % 360;
            angleY.current = angleY.current % 360;
            if (angleY.current < -180) {
                angleY.current = 360 + angleY.current;
            }
            if (angleX.current < -180) {
                angleX.current = 360 + angleX.current;
            }
            ax.current = angleX.current;
            ay.current = angleY.current;
        };
        onMouseWheelRef.current = (e) => {
            if (e.ctrlKey) {
                e.preventDefault();
                return;
            }
            const delta = Math.sign(e.deltaY);
            if (delta < 0) {
                targetScaleRef.current += scaleFactor;
            } else {
                targetScaleRef.current -= scaleFactor;
            }
            targetScaleRef.current = Math.max(0.5, Math.min(30, targetScaleRef.current)
            );
        };
        onKeyDownRef.current = (e) => {
            if (e.key === 'F11') {
                e.preventDefault();
                const isFs = !!document.fullscreenElement;
                if (!isFs) {
                    mountRef.current?.requestFullscreen?.().catch(console.error);
                } else {
                    document.exitFullscreen?.();
                }
            }
        }
        const preventZoom = (e) => {
            if (e.ctrlKey) {
                e.preventDefault();
            }
        };

        window.addEventListener("resize", handleResize);
        canvas.current.addEventListener("mousedown", onMouseDownRef.current);
        window.addEventListener("mousemove", onMouseMoveRef.current);
        window.addEventListener("mouseup", onMouseUpRef.current);
        canvas.current.addEventListener("wheel", onMouseWheelRef.current, { passive: true });
        window.addEventListener("wheel", preventZoom, { passive: false });
        canvas.current.addEventListener('touchstart', on3DTouchStart.current);
        canvas.current.addEventListener('touchmove', on3DTouchMove.current);
        canvas.current.addEventListener('touchend', on3DTouchEnd.current);
        window.addEventListener('keydown', onKeyDownRef.current, { passive: false });

        const animate = () => {
            const current = model.scale.x;
            const target = targetScaleRef.current;
            const newScale = current + (target - current) * 0.1;
            model.scale.set(newScale, newScale, newScale);
            if (!rotateByGizmo.current) {
                if (model) {
                    anglesRef.current.y = model.rotation.y = angleY.current * degree2Radian;
                    anglesRef.current.x = model.rotation.x = angleX.current * degree2Radian;
                }
            }
            if (model) {
                if (!userInteraction.current) {
                    angle.current += 0.25;
                    if (angle.current == 360) angle.current = 0;
                    modelRef.current.rotation.y = (angle.current / 180) * Math.PI;
                    modelRef.current.rotation.x = 0;
                    modelRef.current.rotation.z = 0;
                }
            }
            tweenGroup.current.update();
            renderer.render(scene.current, camera);
            requestAnimationFrame(animate);
        };
        animate();
        return () => {
            window.removeEventListener("resize", handleResize);
            canvas.current.removeEventListener("mousedown", onMouseDownRef.current);
            window.removeEventListener("mousemove", onMouseMoveRef.current);
            window.removeEventListener("mouseup", onMouseUpRef.current);

            canvas.current.removeEventListener("wheel", onMouseWheelRef.current);

            canvas.current.removeEventListener('touchstart', on3DTouchStart.current);
            canvas.current.removeEventListener('touchmove', on3DTouchMove.current);
            canvas.current.removeEventListener('touchend', on3DTouchEnd.current);

            window.removeEventListener("wheel", preventZoom);
            window.removeEventListener('keydown', onKeyDownRef.current, { passive: false });
            container.removeChild(renderer.domElement);
            renderer.dispose();
        };
    }, [state.ready]);
    useEffect(() => {
        if (!bakeCanvas.current) {
            bakeCanvas.current = document.createElement('canvas');
            bakeCanvas.current.width = 256;
            bakeCanvas.current.height = 256;
        }
        ctxBakeCanvas.current = bakeCanvas.current.getContext('2d');
        createBlack();
    }, []);
    useEffect(() => {
        setCollar(state.collar);
    }, [state.collar]);
    useEffect(() => {
        tryApplyTexture();
    }, [state.fabricTexture]);
    useEffect(() => {
        setCuff(state.cuff);
    }, [state.cuff]);
    useEffect(() => {
        setPocket(state.pocket);
    }, [state.pocket]);
    useEffect(() => {
        setPlacket(state.placket);
    }, [state.placket]);
    useEffect(() => {
        setContrast(state.contrast);
    }, [state.contrast]);
    useEffect(() => {
        setTuxedo(state.tuxedo);
    }, [state.tuxedo]);
    useEffect(() => {
        setStitch(state.stitchColor);
    }, [state.stitch, state.stitchColor]);
    useEffect(() => {
        setMonogram(state.monogramLocation);
    }, [state.monogram, state.monogramText, state.monogramLocation, state.monogramFontStyle, state.monogramColor]);
    useEffect(() => {
        tryApplyTexture();
        setTimeout(() => {
            setMonogram(state.monogram);
            setPocket(state.pocket);
            setContrast(state.contrast);
            setStitch(state.stitchColor);
            setTuxedo(state.tuxedo);
            setPlacket(state.placket);
        }, 1000);
    }, [shirtLoaded]);
    function toggleFullscreen(fullscreen) {
        const isFs = !!document.fullscreenElement;
        if (!isFs) {
            mountRef.current?.requestFullscreen?.().catch(console.error);
        } else {
            document.exitFullscreen?.();
        }
    }
    function onFullScreen() {
        canvas.current.removeEventListener("mousedown", onMouseDownRef.current);
        canvas.current.removeEventListener("wheel", onMouseWheelRef.current);
        if (viewFooter.current) {
            viewFooter.current.style.display = "none";
        }
        userInteraction.current = false;
        targetScaleRef.current = 4;
        if (modelRef.current) {
            modelRef.current.scale.set(4, 4, 4);
            modelRef.current.position.set(0.25, 1.85, 0);
            modelRef.current.rotation.set(0, 0, 0);
        }
    }
    function onNormScreen() {
        canvas.current.addEventListener("mousedown", onMouseDownRef.current);
        canvas.current.addEventListener("wheel", onMouseWheelRef.current);
        if (viewFooter.current) {
            viewFooter.current.style.display = "flex";
        }
    }
    function updateMonogram(location = null) {
        if (!shirtLoaded) return;
        while (monoBox.current.children.length != 0) {
            monoBox.current.remove(monoBox.current.children[0]);
        }
        if (!state.monogram) return;
        var loader = new FontLoader();
        let monoFont = (state.monogramFontStyle == "style1") ? "times" : "corsiva";
        loader.load('fonts/' + monoFont + '.json', function (font) {
            const geometry = new TextGeometry(state.monogramText, { bevelEnabled: false, font: font, size: 0.04, depth: 0.005, curveSegments: 12 });
            var material = new THREE.MeshBasicMaterial({ color: getHexById(state.monogramColor) });
            monogram.current = new THREE.Mesh(geometry, material);
            if (location) {
                monogram.current.position.set(location.x, location.y, location.z);
                monogram.current.rotation.set(location.ax * degree2Radian, location.ay * degree2Radian, location.az * degree2Radian);
            }
            else {
                monogram.current.position.set(monoLoc.x, monoLoc.y, monoLoc.z);
                monogram.current.rotation.set(monoLoc.ax * degree2Radian, monoLoc.ay * degree2Radian, monoLoc.az * degree2Radian);
            }
            if (state.monogramLocation == "pocket" && (state.pocket == "Pocket1" || state.pocket == "Pocket2")) {
                monogram.current.position.y = 0.7;
                monogram.current.position.z = 0.53;
                monogram.current.rotation.x = 10 * degree2Radian;
            } else if (state.monogramLocation == "pocket" && state.pocket != "noPocket") {
                monogram.current.position.y = 0.75;
                monogram.current.position.z = 0.5;
                monogram.current.rotation.x = 0 * degree2Radian;
            }
            monogram.current.castShadow = true;
            monoBox.current.add(monogram.current);
        });
        if (state.monogram) {
            dispatch({ type: "SET_MONOGRAM_PRICE", payload: 5 });
        }
    }
    function setMonogram(id) {
        if (shirtLoaded) {
            if (state.monogram) {
                updateMonogram(mono.current[state.monogramLocation])
            } else {
                while (monoBox.current.children.length != 0) {
                    monoBox.current.remove(monoBox.current.children[0]);
                }
            }
        }
    }
    function setCollar(id) {
        if (shirtLoaded) {
            const obj = shirtRef.current.getObjectByName(id);
            for (var i = 0; i < collar3D.current.length; i++) {
                shirtRef.current.getObjectByName(collar3D.current[i]).visible = false;
            }
            if (obj) {
                obj.visible = true;
                shirtRef.current.getObjectByName("Neck").visible = true;
                shirtRef.current.getObjectByName('CollarButton').visible = false;
                shirtRef.current.getObjectByName('CollarHole').visible = false;
                if (id == "CollarMandarian") {
                    shirtRef.current.getObjectByName("Neck").visible = false;
                } else if (id == "CollarButtonDown") {
                    shirtRef.current.getObjectByName('CollarButton').visible = true;
                    shirtRef.current.getObjectByName('CollarHole').visible = true;
                }
            }
        }
    }
    function setCuff(id) {
        if (shirtLoaded) {
            for (var i = 0; i < cuff3D.current.length; i++) {
                shirtRef.current.getObjectByName(cuff3D.current[i]).visible = false;
            }
            if (id == "1ButtonSquare" || id == "2ButtonSquare") {
                shirtRef.current.getObjectByName("CuffStandardSquare").visible = true;
            } else if (id == "1ButtonCurve" || id == "2ButtonCurve") {
                shirtRef.current.getObjectByName("CuffStandardCurve").visible = true;
            } else if (id == "1ButtonAngle" || id == "2ButtonAngle") {
                shirtRef.current.getObjectByName("CuffStandardAngle").visible = true;
            }
            if (id == "frenchSquare") {
                shirtRef.current.getObjectByName("CuffFrenchSquare").visible = true;
            } else if (id == "frenchCurve") {
                shirtRef.current.getObjectByName("CuffFrenchCurve").visible = true;
            } else if (id == "frenchAngle") {
                shirtRef.current.getObjectByName("CuffFrenchAngle").visible = true;
            }
            if (id == "frenchSquare" || id == "frenchCurve" || id == "frenchAngle") {
                shirtRef.current.getObjectByName("ButtonRound").visible = true;
            }
            if (id == "1ButtonSquare" || id == "1ButtonCurve" || id == "1ButtonAngle") {
                shirtRef.current.getObjectByName("CuffButton1").visible = true;
                shirtRef.current.getObjectByName("CuffHole1").visible = true;
            } else if (id == "2ButtonSquare" || id == "2ButtonCurve" || id == "2ButtonAngle") {
                shirtRef.current.getObjectByName("CuffButton2").visible = true;
                shirtRef.current.getObjectByName("CuffHole2").visible = true;
            }
        }
    }
    function setPocket(id) {
        if (shirtLoaded) {
            for (var i = 0; i < pocket3D.current.length; i++) {
                shirtRef.current.getObjectByName(pocket3D.current[i]).visible = false;
            }
            if (id == "Pocket1") {
                shirtRef.current.getObjectByName("Pocket1").visible = true;
            } else if (id == "Pocket2") {
                shirtRef.current.getObjectByName("Pocket1").visible = true;
                shirtRef.current.getObjectByName("Pocket2").visible = true;
            }
            updateMonogram(mono.current[state.monogramLocation]);
        }
    }
    function setStitch(color) {
        if (shirtLoaded) {
            if (color == null) {
                setTexture("fabrics/" + state.fabricCategory + "/" + state.fabricTexture, ['HoleSet1', 'HoleSet2', 'CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck', 'HoleArm'])
                setTimeout(() => {
                    if (state.tuxedo != "noTuxedo") {
                        setItemColor(0x000000, ['HoleSet1', 'ButtonSet1']);
                    }
                }, 1);
            } else {
                setItemColor(getHexById(color), ['HoleSet1', 'HoleSet2', 'CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck', 'HoleArm'])
                setTimeout(() => {
                    if (state.tuxedo != "noTuxedo") {
                        setItemColor(0x000000, ['HoleSet1', 'ButtonSet1']);
                    }
                }, 1);
            }
        }
    }
    function getHexById(id) {
        var colorObject = colors.find(function (color) {
            return color.id === id;
        });

        if (colorObject) {
            return colorObject.hex;
        } else {
            return null;
        }
    }
    function setPlacket(id) {
        if (shirtLoaded) {
            shirtRef.current.getObjectByName('ButtonSet1').visible = true;
            shirtRef.current.getObjectByName('ButtonSet2').visible = true;
            shirtRef.current.getObjectByName('HoleSet1').visible = true;
            shirtRef.current.getObjectByName('HoleSet2').visible = true;
            if (id == "noPlacket") {
                shirtRef.current.getObjectByName("Placket").visible = false;
            } else if (id == "Placket") {
                shirtRef.current.getObjectByName("Placket").visible = true;
            } else if (id == "hiddenPlacket") {
                shirtRef.current.getObjectByName('ButtonSet1').visible = false;
                shirtRef.current.getObjectByName('ButtonSet2').visible = false;
                shirtRef.current.getObjectByName('HoleSet1').visible = false;
                shirtRef.current.getObjectByName('HoleSet2').visible = false;
                shirtRef.current.getObjectByName('Placket').visible = true;
            }
        }
    }
    function setContrast(id) {
        if (shirtLoaded) {
            if (id == "noContrast") {
                setTexture("fabrics" + "/" + state.fabricCategory + "/" + state.fabricTexture, collar3D.current);
                setTexture("fabrics" + "/" + state.fabricCategory + "/" + state.fabricTexture, onlyCuff3D.current);
                setTexture("fabrics" + "/" + state.fabricCategory + "/" + state.fabricTexture, ['Neck', 'CuffButton1', 'CuffButton2', 'CollarButton', 'ButtonNeck', 'CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck']);
                if (state.stitch) {
                    setTimeout(() => {
                        setItemColor(state.stitchColor, ['CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck']);
                    }, 100);
                }
            } else if (id == "whiteCollarCuff") {
                setItemColor(0xffffff, collar3D.current);
                setItemColor(0xffffff, onlyCuff3D.current);
                setItemColor(0xffffff, ['Neck', 'CuffButton1', 'CuffButton2', 'CollarButton', 'ButtonNeck']);
                if (!state.stitch) {
                    setItemColor(0xffffff, ['CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck']);
                } else if (state.stitch) {
                    setItemColor(state.stitchColor, ['CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck']);
                }
            } else if (id == "whiteCollar") {
                setTexture("fabrics" + "/" + state.fabricCategory + "/" + state.fabricTexture, onlyCuff3D.current);
                setTexture("fabrics" + "/" + state.fabricCategory + "/" + state.fabricTexture, ['CuffButton1', 'CuffButton2', 'CuffHole1', 'CuffHole2']);
                setItemColor(0xffffff, collar3D.current);
                setItemColor(0xffffff, ['Neck', 'CollarButton', 'ButtonNeck', 'HoleNeck']);
                setTimeout(() => {
                    if (!state.stitch) {
                        setItemColor(0xffffff, ['CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck']);
                    } else if (state.stitch) {
                        setItemColor(state.stitchColor, ['CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck']);
                    }
                }, 100);

            } else if (id == "whiteCuff") {
                setTexture("fabrics" + "/" + state.fabricCategory + "/" + state.fabricTexture, collar3D.current);
                setTexture("fabrics" + "/" + state.fabricCategory + "/" + state.fabricTexture, ['Neck', 'CollarButton', 'ButtonNeck', 'CollarHole', 'HoleNeck']);
                setItemColor(0xffffff, onlyCuff3D.current);
                setItemColor(0xffffff, ['CuffButton1', 'CuffButton2', 'CuffHole1', 'CuffHole2']);
                setTimeout(() => {
                    if (!state.stitch) {
                        setItemColor(0xffffff, ['CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck']);
                    } else if (state.stitch) {
                        setItemColor(state.stitchColor, ['CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck']);
                    }
                }, 100);
            }
        } else {
            return;
        }
    }
    function setTuxedo(id) {
        if (!shirtRef.current) return;
        if (id == "noTuxedo") {
            shirtRef.current.getObjectByName('Pleats').visible = false;
            setTexture("fabrics/" + state.fabricCategory + "/" + state.fabricTexture, ['ButtonSet1', 'HoleSet1', 'HoleSet2', 'HoleArm']);
            setTimeout(() => {
                if (state.stitch) {
                    setItemColor(state.stitchColor, ['HoleSet1']);
                } else {
                    setTexture("fabrics/" + state.fabricCategory + "/" + state.fabricTexture, ['HoleSet1']);
                }
            }, 100);
            if (state.pocket == 'Pocket1') {
                shirtRef.current.getObjectByName('Pocket1').visible = true;
            } else if (state.pocket == 'Pocket2') {
                shirtRef.current.getObjectByName('Pocket1').visible = true;
                shirtRef.current.getObjectByName('Pocket2').visible = true;
            }
        } else if (id == "stud") {
            shirtRef.current.getObjectByName('Pleats').visible = false;
            setItemColor(0x000000, ['ButtonSet1', 'HoleSet1']);
            if (state.pocket == "Pocket1") {
                shirtRef.current.getObjectByName('Pocket1').visible = true;
            }
            if (state.pocket == "Pocket2") {
                shirtRef.current.getObjectByName('Pocket1').visible = true;
                shirtRef.current.getObjectByName('Pocket2').visible = true;
            }
        } else if (id == "studPleat") {
            shirtRef.current.getObjectByName('Pleats').visible = true;
            shirtRef.current.getObjectByName('Pocket1').visible = false;
            shirtRef.current.getObjectByName('Pocket2').visible = false;
            setItemColor(0x000000, ['ButtonSet1', 'HoleSet1']);
        }
        if (id == "studPleat" && state.applyMono && (state.pocket == "Pocket1" || state.pocket == "Pocket2")) {
            while (monoBox.current.children.length != 0) {
                monoBox.current.remove(monoBox.current.children[0]);
            }
        } else if (id != "studPleat" && state.applyMono && (state.pocket == "Pocket1" || state.pocket == "Pocket2")) {
            updateMonogram();
        }
    }
    function setItemColor(color, list) {
        var mat = new THREE.MeshStandardMaterial({ color: color, side: THREE.DoubleSide, });
        for (let i = 0; i < list.length; i++) {
            shirtRef.current.getObjectByName(list[i]).material = mat;
        }
    }
    function createBlack() {
        bakeCanvas.current.style.width = '256px';
        bakeCanvas.current.style.height = '256px';
        black100.current = new Image();
        black100.current.crossOrigin = 'anonymous';
        black100.current.onload = function () {
            black50.current = new Image();
            black50.current.crossOrigin = 'anonymous';
            black50.current.onload = function () {
                ctxBakeCanvas.current = bakeCanvas.current.getContext('2d');
                bakeReady.current = true;
                tryApplyTexture();
            };
            black50.current.src = process.env.PUBLIC_URL + '/images/black50.png';
        };
        black100.current.src = process.env.PUBLIC_URL + '/images/black100.jpg';
    }
    function tryApplyTexture() {
        if (shirtLoaded) {
            if (!bakeReady.current) return;
            setTexture("fabrics/" + state.fabricCategory + "/" + state.fabricTexture, all.current);
            setTimeout(() => {
                setContrast(state.contrast)
                if (state.stitch) {
                    setItemColor(getHexById(state.stitchColor), ['HoleSet1', 'HoleSet2', 'CuffHole1', 'CuffHole2', 'CollarHole', 'HoleNeck', 'HoleArm']);
                }
                if (state.tuxedo == "stud" || state.tuxedo == "studPleat") {
                    setItemColor(0x000000, ['ButtonSet1', 'HoleSet1']);
                }
            }, 100);
        }
    }
    function setTexture(texture, list) {
        bakeImage.current = new Image();
        bakeImage.current.onload = function () {
            bakeCanvas.current.width = bakeImage.current.width;
            bakeCanvas.current.height = bakeImage.current.height;
            ctxBakeCanvas.current.drawImage(bakeImage.current, 0, 0);
            ctxBakeCanvas.current.globalCompositeOperation = 'overlay';
            ctxBakeCanvas.current.drawImage(black100.current, 0, 0);
            ctxBakeCanvas.current.globalCompositeOperation = 'overlay';
            ctxBakeCanvas.current.drawImage(black50.current, 0, 0);

            var texture = new THREE.CanvasTexture(bakeCanvas.current);
            texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            texture.offset.set(0, 0);
            var tile = 8;
            texture.repeat.set(tile, tile);
            var mat = new THREE.MeshStandardMaterial({
                map: texture,
                side: THREE.DoubleSide,
            });
            bakedFabric.current = mat;
            for (var i = 0; i < list.length; i++) {
                shirtRef.current.getObjectByName(list[i]).material = mat;
            }
        };
        bakeImage.current.src = process.env.PUBLIC_URL + "/" + texture;
    }
    function tweenAngles(targetX, targetY, targetZ, duration = 1000) {
        const start = { x: angleX.current, y: angleY.current, z: angleZ.current };
        const end = { x: targetX, y: targetY, z: targetZ };
        new Tween(start, tweenGroup.current)
            .to(end, duration)
            .easing(Easing.Quadratic.InOut)
            .onUpdate(() => {
                angleX.current = start.x;
                angleY.current = start.y;
                angleZ.current = start.z;
            })
            .onComplete(() => {
                ax.current = angleX.current;
                ay.current = angleY.current;
                rotateByGizmo.current = false;
            })
            .start();
    }
    function onNavClick(id) {
        setTrans(id);
        setActiveTrans(id);
        if (id == "spin") {
            userInteraction.current = false;
            targetScaleRef.current = 4;
            modelScaleRef.current = 4;
            angleX.current = 0;
            angleY.current = 0;
            angleZ.current = 0;
            if (modelRef.current) {
                angle.current = 0;
                modelRef.current.scale.set(4, 4, 4);
                modelRef.current.position.set(0.25, 1.85, 0);
                modelRef.current.rotation.set(0, 0, 0);
            }
        } else {
            transform.current = id;
        }
    }
    function loadModel() {
        const loader = new GLTFLoader();
        loader.load(
            process.env.PUBLIC_URL + "/models/Shirt.glb",
            (gltf) => {
                const shirt = gltf.scene;
                shirtRef.current = shirt;
                modelRef.current.add(shirt);
                modelRef.current.position.set(0.25, 1.85, 0);
                modelRef.current.scale.set(4, 4, 4);
                shirt.traverse((node) => {
                    if (node.isMesh) {
                        node.frustumCulled = false;
                        node.castShadow = true;
                        node.receiveShadow = true;
                        node.visible = state.visibleMesh.includes(node.name);
                    }
                });
                modelRef.current.traverse((node) => {
                    if (node.material) {
                        node.material.side = THREE.DoubleSide;
                    }
                });
                setShirtLoaded(true);
            },
            function (xhr) { },
            (err) => console.error(err)
        );
    }
    function onTileClick(id) {
        dispatch({ type: "SET_FABRIC_VIEW", payload: id })
    }

    return (
        <div className="view3D" ref={mountRef}>
            <canvas ref={bakeCanvas} style={{ display: "none" }}></canvas>
            <div ref={viewFooter} className="viewFooter">
                <div onMouseEnter={(e) => showTooltip(e, "Orbit Gizmo")} onMouseLeave={hideTooltip} className="divGizmo">
                    <Gizmo
                        angles={anglesRef.current}
                        onPlaneClick={(rotation) => {
                            if (modelRef.current) {
                                tweenAngles(rotation.x * radian2Degree, rotation.y * radian2Degree, rotation.z * radian2Degree, 1000);
                            }
                        }}
                    />
                </div>
                <div className="divNav">

                    <img id="spin" onMouseEnter={(e) => showTooltip(e, "Spin")} onMouseLeave={hideTooltip} className="navIcon" src={(trans == "spin") ? process.env.PUBLIC_URL + "/images/spinOn.svg" : process.env.PUBLIC_URL + "/images/spin.svg"} onClick={() => onNavClick("spin")} />
                    <img id="rotate" onMouseEnter={(e) => showTooltip(e, "Rotate")} onMouseLeave={hideTooltip} className="navIcon" src={(trans == "rotate") ? process.env.PUBLIC_URL + "/images/rotateOn.svg" : process.env.PUBLIC_URL + "/images/rotate.svg"} onClick={() => onNavClick("rotate")} />
                    <img id="drag" onMouseEnter={(e) => showTooltip(e, "Drag")} onMouseLeave={hideTooltip} className="navIcon" src={(trans == "drag") ? process.env.PUBLIC_URL + "/images/dragOn.svg" : process.env.PUBLIC_URL + "/images/drag.svg"} onClick={() => onNavClick("drag")} />
                    <div className="tileViewer" style={{ display: state.orientation == "landscape" ? "none" : "flex" }}>
                        <img onClick={() => { onTileClick("shirt") }} className="tileImage" src={process.env.PUBLIC_URL + "/fabrics/" + state.fabricCategory + "/" + state.fabricShirt} />
                        <img onClick={() => { onTileClick("tile") }} style={{ padding: "0 5px" }} className="tileImage" src={process.env.PUBLIC_URL + "/fabrics/" + state.fabricCategory + "/" + state.fabricTile} />
                    </div>
                </div>
            </div>
            <ViewHeader onToggleFullscreen={toggleFullscreen} isFullscreen={isFullscreen} />
        </div>
    );
}
export default View3D;
